import { ReactNode, useEffect, useState } from 'react';

import { GetAvailableRoutes } from 'containers/AppRouter';
import { PAGE_FLOWS } from 'containers/Flows';
import { PAGE_IP_NETWORKS } from 'containers/IPNetworks';
import { PAGE_ORGANISATIONS } from 'containers/Organizations';
import { PAGE_SOURCES } from 'containers/Sources';
import { ConditionalLink } from 'shared/components/Conditional';
import TableRender, { TableRenderFieldT } from 'shared/components/Table/TableRender';
import * as SearchM from 'shared/models/search';
import { SearchT } from 'shared/models/search';
import api from 'utils/api';
import { useAuth } from 'utils/hooks/providers/auth-context';

import './Search.scss';

function Search() {
  const { authState } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const urlParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    const value = urlParams.get('search') || '';
    setSearchQuery(value);
  }, []);

  const linkOnAutorize = (link: string): string => {
    const routesAvailables = GetAvailableRoutes(authState);
    const routePrefix = link.split('/').filter((i) => i)[0]; // Remove empty boxes
    if (routesAvailables?.includes(`/${routePrefix}`)) {
      return link;
    }
    return '';
  };

  const availableFields: Array<TableRenderFieldT> = [
    {
      displayName: SearchM.LABEL_FLOW,
      renderFn: (row: SearchT) => row.scenarioName,
      linkFn: (row: SearchT) => (row.scenarioID ? linkOnAutorize(`${PAGE_FLOWS}/${row.scenarioID}`) : ''),
    },
    {
      displayName: SearchM.LABEL_IP_NETWORK,
      renderFn: (row: SearchT) => row.iPNetworkName,
      linkFn: (row: SearchT) => (row.iPNetworkID ? linkOnAutorize(`${PAGE_IP_NETWORKS}/${row.iPNetworkID}`) : ''),
    },
    {
      displayName: SearchM.LABEL_DEVICE,
      renderFn: (row: SearchT) => row.deviceName,
      linkFn: (row: SearchT) => (row.deviceName ? linkOnAutorize(`${PAGE_FLOWS}/${row.scenarioID}/device-profiles/${row.deviceProfileID}/devices/${row.deviceConfigID}`) : ''),
    },
    {
      displayName: SearchM.LABEL_DEVICE_PROFILE,
      renderFn: (row: SearchT) => row.deviceProfileName,
      linkFn: (row: SearchT) => (row.deviceProfileName ? linkOnAutorize(`${PAGE_FLOWS}/${row.scenarioID}/device-profiles/${row.deviceProfileID}`) : ''),
    },
    {
      displayName: SearchM.LABEL_SCHC_TEMPLATE,
      renderFn: (row: SearchT) => row.templateName,
      linkFn: (row: SearchT) => (row.templateName ? `/schc-templates/${row.templateID}` : ''),
    },
    {
      displayName: SearchM.LABEL_SOURCE,
      renderFn: (row: SearchT) => row.sourceName,
      linkFn: (row: SearchT) => (row.sourceID ? linkOnAutorize(`${PAGE_SOURCES}/${row.sourceID}`) : ''),
    },
  ];

  const renderLinkedElements = (row: SearchT): ReactNode => {
    const groupedFields: ReactNode[] = [];
    const typeLabel = SearchM.TypeToLabel[row.type];
    availableFields.forEach((field, index) => {
      const displayValue = field.renderFn(row);
      if (typeLabel !== field.displayName && displayValue) {
        groupedFields.push(
          <div key={`${row.type}-${+index}`}>
            <p className="search-results__grouped-fields--label">
              { field.displayName }:
            </p>
            <ConditionalLink link={field.linkFn && field.linkFn(row)}>
              <p>
                { displayValue }
              </p>
            </ConditionalLink>
          </div>,
        );
      }
    });
    return (
      <div className="search-results__grouped-fields">
        { groupedFields }
      </div>
    );
  };

  // getName: Get the TableRenderFieldT according the type to fill the name columns
  const getName = (row: SearchT): TableRenderFieldT => {
    const label = SearchM.TypeToLabel[row.type];
    const findField = availableFields.find((field) => field.displayName === label);
    if (findField) return findField;
    return {
      displayName: '',
      renderFn: () => '',
    };
  };

  const renderName = (row: SearchT) => {
    const nameDetails = getName(row);
    if (nameDetails.renderFn) return nameDetails.renderFn(row);
    return '';
  };
  const linkName = (row: SearchT) => {
    const nameDetails = getName(row);
    if (nameDetails.linkFn) return nameDetails.linkFn(row);
    return '';
  };

  const fields: TableRenderFieldT[] = [
    {
      displayName: 'Type',
      renderFn: (row: SearchT) => SearchM.TypeToLabel[row.type],
    },
    {
      // Name is corresponding to the name with the matching type
      displayName: 'Name',
      renderFn: renderName,
      linkFn: linkName,
      align: 'center',
    },
    {
      displayName: 'Linked elements',
      renderFn: renderLinkedElements,
      align: 'center',
    },
  ];

  const fieldOrg: TableRenderFieldT[] = [
    {
      displayName: 'Organization',
      renderFn: (row: SearchT) => row.organizationName,
      align: 'center',
      linkFn: (row: SearchT) => (row.organizationName ? linkOnAutorize(`${PAGE_ORGANISATIONS}/${row.organizationName}`) : ''),
    },
  ];

  if (searchQuery.length < 3) {
    return null;
  }
  return (
    <div>
      <div className="title__header">
        <div className="title__header--block" style={{ alignItems: 'baseline' }}>
          <h1>Search Results</h1>
        </div>
      </div>
      <TableRender
        api={api.search}
        apiParams={{ search: searchQuery }}
        renderFields={[
          ...fields,
          ...(authState.organization ? [] : fieldOrg), // org field only displayed in multiorg
        ]}
        hasNoPagination
      />
    </div>
  );
}

export default Search;
